// src/features/form/FieldEditorPanel.tsx
// Field settings editor for the form builder — built on the WIP form-kit
// (FormRow, InlineEditText, SegmentedControl) plus ToggleSwitch/Select for
// the property controls, per docs/ux/14-forms.md field rules:
// enum <=4 -> segmented control, enum >=5 -> Select, numbers clearable/no steppers.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { FormRow, InlineEditText } from '@/components/form';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { FormField, FormFieldOption, FormFieldType } from '@core/types/form';
import { FIELD_TYPE_ORDER, OPTION_FIELD_TYPES, parseClearableInt } from './helpers';

export interface FieldEditorPanelProps {
  field: FormField;
  isSaving?: boolean;
  onChange: (patch: Partial<FormField>) => void;
  onDelete: () => void;
}

function typeLabel(t: (k: string) => string, type: FormFieldType): string {
  return t(`form.fieldType.${type.replace('FORM_FIELD_TYPE_', '').toLowerCase()}`);
}

export function FieldEditorPanel({ field, isSaving, onChange, onDelete }: FieldEditorPanelProps) {
  const { t } = useI18n();
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const validation = field.validation ?? {
    required: false,
    min_length: 0,
    max_length: 0,
    min_value: 0,
    max_value: 0,
    pattern: '',
    custom_error: '',
    allowed_file_types: [],
    max_file_size_bytes: 0,
  };

  const setValidation = (patch: Partial<typeof validation>) => {
    onChange({ validation: { ...validation, ...patch } });
  };

  const isTextLike = ['FORM_FIELD_TYPE_TEXT', 'FORM_FIELD_TYPE_TEXTAREA', 'FORM_FIELD_TYPE_EMAIL', 'FORM_FIELD_TYPE_PHONE', 'FORM_FIELD_TYPE_URL'].includes(
    field.field_type ?? ''
  );
  const isNumericLike = ['FORM_FIELD_TYPE_NUMBER', 'FORM_FIELD_TYPE_RATING', 'FORM_FIELD_TYPE_SCALE', 'FORM_FIELD_TYPE_NPS'].includes(
    field.field_type ?? ''
  );
  const isFileUpload = field.field_type === 'FORM_FIELD_TYPE_FILE_UPLOAD';
  const hasOptions = OPTION_FIELD_TYPES.includes(field.field_type ?? 'FORM_FIELD_TYPE_UNSPECIFIED');

  const options = field.options ?? [];

  const updateOption = (index: number, patch: Partial<FormFieldOption>) => {
    const next = options.map((o, i) => (i === index ? { ...o, ...patch } : o));
    onChange({ options: next });
  };
  const removeOption = (index: number) => {
    onChange({ options: options.filter((_, i) => i !== index) });
  };
  const addOption = () => {
    if (!newOptionLabel.trim()) return;
    const value = newOptionLabel.trim().toLowerCase().replace(/\s+/g, '_');
    onChange({
      options: [...options, { value, label: newOptionLabel.trim(), is_default: false, order: options.length }],
    });
    setNewOptionLabel('');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <InlineEditText
            value={field.label}
            onCommit={(v) => onChange({ label: v })}
            placeholder={t('form.builder.fieldLabelPlaceholder')}
            variant="title"
            aria-label={t('form.builder.fieldLabel')}
          />
        </div>
        <Button size="sm" variant="danger" onClick={onDelete} disabled={isSaving}>
          {t('common.delete')}
        </Button>
      </div>

      <FormRow label={t('form.builder.fieldType')} labelWidth="sm">
        <Select
          value={field.field_type ?? 'FORM_FIELD_TYPE_TEXT'}
          onChange={(e) => onChange({ field_type: e.target.value as FormFieldType })}
          options={FIELD_TYPE_ORDER.map((v) => ({ value: v, label: typeLabel(t, v) }))}
          aria-label={t('form.builder.fieldType')}
        />
      </FormRow>

      <FormRow label={t('form.builder.placeholder')} labelWidth="sm">
        <InlineEditText
          value={field.placeholder}
          onCommit={(v) => onChange({ placeholder: v })}
          placeholder={t('form.builder.placeholderEmpty')}
          allowEmpty
          aria-label={t('form.builder.placeholder')}
        />
      </FormRow>

      <FormRow label={t('form.builder.helpText')} labelWidth="sm">
        <InlineEditText
          value={field.help_text}
          onCommit={(v) => onChange({ help_text: v })}
          placeholder={t('form.builder.helpTextEmpty')}
          allowEmpty
          aria-label={t('form.builder.helpText')}
        />
      </FormRow>

      <FormRow label={t('form.builder.required')} labelWidth="sm">
        <ToggleSwitch label="" checked={validation.required} onChange={(v) => setValidation({ required: v })} />
      </FormRow>

      {isTextLike && (
        <>
          <FormRow label={t('form.builder.minLength')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={validation.min_length || ''}
              onChange={(e) => setValidation({ min_length: parseClearableInt(e.target.value) ?? 0 })}
              placeholder={t('form.builder.unset')}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </FormRow>
          <FormRow label={t('form.builder.maxLength')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={validation.max_length || ''}
              onChange={(e) => setValidation({ max_length: parseClearableInt(e.target.value) ?? 0 })}
              placeholder={t('form.builder.unset')}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </FormRow>
          <FormRow label={t('form.builder.pattern')} labelWidth="sm">
            <InlineEditText
              value={validation.pattern}
              onCommit={(v) => setValidation({ pattern: v })}
              placeholder={t('form.builder.patternEmpty')}
              allowEmpty
              aria-label={t('form.builder.pattern')}
            />
          </FormRow>
        </>
      )}

      {isNumericLike && (
        <>
          <FormRow label={t('form.builder.minValue')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={validation.min_value || ''}
              onChange={(e) => setValidation({ min_value: parseClearableInt(e.target.value) ?? 0 })}
              placeholder={t('form.builder.unset')}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </FormRow>
          <FormRow label={t('form.builder.maxValue')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={validation.max_value || ''}
              onChange={(e) => setValidation({ max_value: parseClearableInt(e.target.value) ?? 0 })}
              placeholder={t('form.builder.unset')}
              className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </FormRow>
        </>
      )}

      {isFileUpload && (
        <>
          <FormRow label={t('form.builder.allowedFileTypes')} labelWidth="sm">
            <InlineEditText
              value={validation.allowed_file_types.join(', ')}
              onCommit={(v) => setValidation({ allowed_file_types: v.split(',').map((s) => s.trim()).filter(Boolean) })}
              placeholder={t('form.builder.allowedFileTypesEmpty')}
              allowEmpty
              aria-label={t('form.builder.allowedFileTypes')}
            />
          </FormRow>
          <FormRow label={t('form.builder.maxFileSize')} labelWidth="sm">
            <input
              type="text"
              inputMode="numeric"
              value={validation.max_file_size_bytes || ''}
              onChange={(e) => setValidation({ max_file_size_bytes: parseClearableInt(e.target.value) ?? 0 })}
              placeholder={t('form.builder.unset')}
              className="w-32 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
            />
          </FormRow>
        </>
      )}

      {hasOptions && (
        <FormRow label={t('form.builder.options')} labelWidth="sm">
          <div className="space-y-1.5">
            {options.map((o, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={o.label}
                  onChange={(e) => updateOption(i, { label: e.target.value })}
                  className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground"
                />
                <button
                  type="button"
                  onClick={() => removeOption(i)}
                  aria-label={t('common.delete')}
                  className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                >
                  ✕
                </button>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <input
                type="text"
                value={newOptionLabel}
                onChange={(e) => setNewOptionLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addOption();
                  }
                }}
                placeholder={t('form.builder.addOption')}
                className="flex-1 rounded-md border border-dashed border-input bg-background px-2 py-1 text-sm text-foreground"
              />
              <Button size="sm" variant="secondary" onClick={addOption} disabled={!newOptionLabel.trim()}>
                {t('common.add')}
              </Button>
            </div>
          </div>
        </FormRow>
      )}
    </div>
  );
}
